import { useEffect, useRef } from "react";
import { Platform } from "react-native";
import checkKey from "./keys";

type KeydownProps = {
  isActive: boolean;
  isOffline?: boolean;
  disableRtl?: boolean;
  number?: (e: KeyboardEvent) => void;
  anyKey?: (e: KeyboardEvent) => void;
  [key: string]: ((e: KeyboardEvent) => void) | boolean | undefined;
};

const canUseDOM =
  Platform.OS === "web" &&
  typeof window !== "undefined" &&
  typeof window.addEventListener === "function";

const isRTL = () =>
  canUseDOM ? document.documentElement.dir === "rtl" : false;

const isRtlControl = (key: string) => {
  if (!isRTL()) return key;
  if (key === "left") return "right";
  if (key === "right") return "left";

  return key;
};

function useKeydown(props: KeydownProps) {
  const propsRef = useRef(props);

  useEffect(() => {
    propsRef.current = props;
  });

  useEffect(() => {
    if (!props.isActive) return undefined;
    if (!canUseDOM) return undefined;

    const handleKeydown = (e: KeyboardEvent) => {
      const currentProps = propsRef.current;
      const isOffline =
        typeof sessionStorage !== "undefined"
          ? sessionStorage.getItem("isOffline") === "true"
          : false;

      if (isOffline && !currentProps.isOffline) {
        e.preventDefault();
        return;
      }
      let key;

      // @ts-ignore
      if (e?.detail?.keyName) {
        // @ts-ignore
        key = e.detail.keyName;
      } else {
        e.preventDefault();
        key = checkKey(e);
        if (!propsRef.current.disableRtl) {
          key = isRtlControl(key);
        }
      }

      if (currentProps.anyKey) {
        currentProps.anyKey(key);
      }

      if (key && !Number.isNaN(Number(key)) && currentProps.number) {
        (e as unknown as { numeric: string }).numeric = key;
        key = "number";
      }

      if (typeof currentProps[key] === "function") {
        (currentProps[key] as (e: KeyboardEvent) => void)(e);
      }
    };

    window.addEventListener("keydown", handleKeydown);

    return () => {
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [props.isActive]);
}

export default useKeydown;
